import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import * as XLSX from "xlsx";

import LevelFilter from "./LevelFilter";
import ActionButtons from "./ActionButtons";
import { UserLevel, MonthOptions } from "utils/constant";
import InputRadio from "components/atoms/InputRadio";
import InputDate from "components/atoms/InputDate";
import InputSelect from "components/atoms/InputSelect";

const isValidYYYY = (v) => v !== undefined && /^\d{4}$/.test(String(v));
const isValidMM = (v) => typeof v === "string" && /^(0[1-9]|1[0-2])$/.test(v);

/** Generate month options starting from month_start to December */
const generateMonthOptions = (start) => {
  if (!start) return MonthOptions;
  const startIdx = MonthOptions.findIndex((m) => m.value === start);
  if (startIdx < 0) return MonthOptions;
  return MonthOptions.slice(startIdx);
};

const FilterSection = ({
  currentUser,
  selectedLevel,
  setSelectedLevel,
  levelData,
  levelDataOptions,
  setLevelIdValue,
  selectedProvince,
  setSelectedProvince,
  selectedDistrict,
  setSelectedDistrict,
  selectedFasyankes,
  setSelectedFasyankes,
  handleApplyFilter,
  tableRef,
  showData,
  showFilter,
  setShowFilter,
  levelIdValue,
  reportName = "LAPORAN_REKAP1_PEMANTAUAN_JENTIK",
}) => {
  const [periodType, setPeriodType] = useState("year");
  const [year, setYear] = useState("");

  const [periodeLaporanValue, setPeriodeLaporanValue] = useState({
    year: "",
    month_start: "",
    month_end: "",
  });

  const [selectedStart, setSelectedStart] = useState("");
  const [selectedEnd, setSelectedEnd] = useState("");

  useEffect(() => {
    if (currentUser.level === UserLevel.Fasyankes) {
      setLevelIdValue(currentUser?.fasyankes_id);
    } else if (currentUser.level === UserLevel.District) {
      setSelectedFasyankes("");
      setLevelIdValue(currentUser?.district_id);
    } else if (currentUser.level === UserLevel.Province) {
      setSelectedDistrict("");
      setSelectedFasyankes("");
      setLevelIdValue(currentUser?.province_id);
    } else if (currentUser.level === UserLevel.National) {
      setSelectedProvince("");
      setSelectedDistrict("");
      setSelectedFasyankes("");
    } else {
      setLevelIdValue("");
    }
  }, [selectedLevel]);

  const levelLabel = useMemo(
    () =>
      selectedLevel === UserLevel.National
        ? UserLevel.National
        : levelIdValue?.split("|")[1],
    [selectedLevel, levelIdValue]
  );

  const isLevelChosen = () =>
    (selectedLevel === UserLevel.Fasyankes && !!selectedFasyankes) ||
    (selectedLevel === UserLevel.District && !!selectedDistrict) ||
    (selectedLevel === UserLevel.Province && !!selectedProvince) ||
    selectedLevel === UserLevel.National;

  const startPeriodStr = useMemo(() => {
    const y = periodeLaporanValue.year;
    const m = selectedStart || periodeLaporanValue.month_start;
    return isValidYYYY(y) && isValidMM(m) ? `${y}-${m}` : "";
  }, [
    periodeLaporanValue.year,
    periodeLaporanValue.month_start,
    selectedStart,
  ]);

  const endPeriodStr = useMemo(() => {
    const y = periodeLaporanValue.year;
    const m = selectedEnd || periodeLaporanValue.month_end;
    return isValidYYYY(y) && isValidMM(m) ? `${y}-${m}` : "";
  }, [periodeLaporanValue.year, periodeLaporanValue.month_end, selectedEnd]);

  const isPeriodValid = () => {
    if (periodType === "year") return isValidYYYY(year);
    if (!startPeriodStr || !endPeriodStr) return false;
    return startPeriodStr <= endPeriodStr;
  };

  const isEnableButton = () => isLevelChosen() && isPeriodValid();

  const buildPayload = () => {
    const payload = { level: selectedLevel };

    if (selectedLevel === UserLevel.Fasyankes)
      payload.level_id = selectedFasyankes;
    if (selectedLevel === UserLevel.District)
      payload.level_id = selectedDistrict;
    if (selectedLevel === UserLevel.Province)
      payload.level_id = selectedProvince;

    if (periodType === "year") {
      payload.period = "year";
      payload.year = Number(year);
    } else {
      payload.period = "range";
      payload.start_period = startPeriodStr;
      payload.end_period = endPeriodStr;
    }

    return payload;
  };

  const exportToExcel = () => {
    const table = tableRef.current;
    const wb = XLSX.utils.table_to_book(table, { sheet: "Sheet 1" });

    const periodSuffix =
      periodType === "year"
        ? `Y${year}`
        : `R${startPeriodStr.replace("-", "")}-${endPeriodStr.replace(
            "-",
            ""
          )}`;

    XLSX.writeFile(
      wb,
      `${reportName}_${levelLabel}_${periodSuffix}_${moment().format(
        "DDMMYYYY"
      )}.xlsx`
    );
  };

  return (
    <div className="filter">
      <div className="filter-header">
        <p>FILTER</p>
        <button
          className="btn_toggle_filter"
          onClick={() => setShowFilter(!showFilter)}
        >
          {showFilter ? <span>&#9650;</span> : <span>&#9660;</span>}
        </button>
      </div>

      {showFilter && (
        <div className="filter-body">
          <div className="filter-item">
            <div className="filter-label">
              <p>Periode</p>
            </div>
            <div className="filter-field">
              <InputRadio
                isRequired={true}
                options={[
                  { label: "Tahunan", value: "year" },
                  { label: "Bulanan", value: "range" },
                ]}
                onChange={(e) => setPeriodType(e.target.value)}
                selectedOption={periodType}
              />

              {periodType === "year" && (
                <div style={{ marginTop: 12 }}>
                  <InputDate
                    value={year ? moment(String(year), "YYYY") : null}
                    onChange={(e) => {
                      const selectedYear = moment(e).format("YYYY");
                      setYear(Number(selectedYear));
                    }}
                    placeholder={"Pilih Tahun"}
                    dateFormat={"YYYY"}
                  />
                </div>
              )}

              {periodType === "range" && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "1.2rem",
                  }}
                >
                  <div style={{ flex: "1" }}>
                    <InputDate
                      value={
                        periodeLaporanValue.year
                          ? moment(String(periodeLaporanValue.year), "YYYY")
                          : null
                      }
                      onChange={(e) => {
                        const selectedYear = moment(e).format("YYYY");
                        setPeriodeLaporanValue((prev) => ({
                          ...prev,
                          year: selectedYear,
                        }));
                      }}
                      placeholder={"Pilih Tahun"}
                      dateFormat={"YYYY"}
                    />
                  </div>

                  <div style={{ flex: "1" }}>
                    <InputSelect
                      data={MonthOptions}
                      isClearable={false}
                      placeholder={"Bulan Mulai"}
                      onChange={(e) => {
                        setPeriodeLaporanValue((prev) => ({
                          ...prev,
                          month_start: e.value,
                          month_end: "",
                        }));
                        setSelectedStart(e.value);
                        setSelectedEnd("");
                      }}
                      value={
                        MonthOptions.find((m) => m.value === selectedStart) ||
                        ""
                      }
                    />
                  </div>

                  <div style={{ flex: "1" }}>
                    <InputSelect
                      data={generateMonthOptions(
                        periodeLaporanValue.month_start
                      )}
                      isClearable={false}
                      placeholder={"Bulan Akhir"}
                      onChange={(e) => {
                        setPeriodeLaporanValue((prev) => ({
                          ...prev,
                          month_end: e.value,
                        }));
                        setSelectedEnd(e.value);
                      }}
                      value={
                        generateMonthOptions(
                          periodeLaporanValue.month_start
                        )?.find((item) => item.value === selectedEnd) || ""
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="filter-item">
            <div className="filter-label">
              <p>Level</p>
            </div>
            <div className="filter-field">
              <LevelFilter
                currentUser={currentUser}
                selectedLevel={selectedLevel}
                setSelectedLevel={setSelectedLevel}
                levelData={levelData}
                levelDataOptions={levelDataOptions}
                setLevelIdValue={setLevelIdValue}
                selectedProvince={selectedProvince}
                setSelectedProvince={setSelectedProvince}
                selectedDistrict={selectedDistrict}
                setSelectedDistrict={setSelectedDistrict}
                selectedFasyankes={selectedFasyankes}
                setSelectedFasyankes={setSelectedFasyankes}
              />
            </div>
          </div>

          <div className="filter-item">
            <div className="filter-field">
              <ActionButtons
                onApply={() => handleApplyFilter(buildPayload())}
                onExport={exportToExcel}
                isExportVisible={showData}
                isDisabled={!isEnableButton()}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterSection;
